import { useEffect, useRef } from 'react';

import { Button } from './Button';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation gate for irreversible director actions (reset, delete).
 *
 * Built on the native `<dialog>` so focus trapping, Escape-to-close and the
 * backdrop come from the platform rather than from hand-written key handling.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  tone = 'primary',
  isBusy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
      className="bg-surface text-fg border-line m-auto w-[min(28rem,92vw)] rounded-xl border p-0 backdrop:bg-black/60"
    >
      <div className="flex flex-col gap-4 p-6">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-fg-muted text-sm">{description}</p>
        <div className="mt-2 flex justify-end gap-3">
          <Button variant="ghost" size="md" onClick={onCancel} disabled={isBusy}>
            Abbrechen
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="md"
            onClick={onConfirm}
            isLoading={isBusy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
